import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { sermonsApi } from '@/lib/api';
import { Plus, Edit, Trash2, Video, Upload, X, ExternalLink, Loader2, CheckCircle2, Youtube, Import } from 'lucide-react';
import { toast } from 'sonner';

const AdminSermons = () => {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [editingSermon, setEditingSermon] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [selectedSermons, setSelectedSermons] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  
  // File upload state
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // '', 'uploading', 'processing', 'done', 'error'
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    description: '',
    speaker: 'Pastor Wendell Thacker',
    video_url: '',
    thumbnail_url: '',
    duration: ''
  });

  useEffect(() => {
    loadSermons();
  }, []);

  const loadSermons = async () => {
    try {
      const data = await sermonsApi.getAll();
      // Sort by date, newest first
      data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setSermons(data);
      setSelectedSermons(new Set()); // Clear selection when reloading
    } catch (error) {
      console.error('Error loading sermons:', error);
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedSermons.size === sermons.length) {
      setSelectedSermons(new Set());
    } else {
      setSelectedSermons(new Set(sermons.map(s => s.id)));
    }
  };

  const toggleSelectSermon = (id) => {
    const newSelected = new Set(selectedSermons);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedSermons(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedSermons.size === 0) return;
    
    const count = selectedSermons.size;
    if (!window.confirm(`Are you sure you want to delete ${count} sermon${count > 1 ? 's' : ''}? This cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    let deleted = 0;
    let failed = 0;

    for (const id of selectedSermons) {
      try {
        await sermonsApi.delete(id);
        deleted++;
      } catch (error) {
        console.error(`Failed to delete sermon ${id}:`, error);
        failed++;
      }
    }

    setIsDeleting(false);
    setSelectedSermons(new Set());
    
    if (failed > 0) {
      toast.error(`Deleted ${deleted} sermons, ${failed} failed`);
    } else {
      toast.success(`Deleted ${deleted} sermon${deleted > 1 ? 's' : ''}`);
    }
    
    loadSermons();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      description: '',
      speaker: 'Pastor Wendell Thacker',
      video_url: '',
      thumbnail_url: '',
      duration: ''
    });
    setEditingSermon(null);
    setVideoFile(null);
    setThumbnailFile(null);
    setUploadProgress(0);
    setUploadStatus('');
    
    // Reset file inputs
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  const handleEdit = (sermon) => {
    setEditingSermon(sermon);
    setFormData({
      title: sermon.title,
      date: sermon.date?.split('T')[0] || '',
      description: sermon.description || '',
      speaker: sermon.speaker || 'Pastor Wendell Thacker',
      video_url: sermon.video_url || '',
      thumbnail_url: sermon.thumbnail_url || '',
      duration: sermon.duration || ''
    });
    setVideoFile(null);
    setThumbnailFile(null);
    setDialogOpen(true);
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Thumbnail must be less than 10MB');
      return;
    }

    setThumbnailFile(file);
    setFormData({ ...formData, thumbnail_url: '' }); // Clear URL if file selected
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // 3GB limit for 75-minute videos
    if (file.size > 3 * 1024 * 1024 * 1024) {
      toast.error('Video must be less than 3GB');
      return;
    }

    setVideoFile(file);
    setFormData({ ...formData, video_url: '' }); // Clear URL if file selected
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let sermonId;
      
      // Step 1: Create or update sermon metadata
      if (editingSermon) {
        await sermonsApi.update(editingSermon.id, formData);
        sermonId = editingSermon.id;
        toast.success('Sermon details updated');
      } else {
        const newSermon = await sermonsApi.create(formData);
        sermonId = newSermon.id;
        toast.success('Sermon created');
      }
      
      // Step 2: Upload video file if selected
      if (videoFile) {
        setUploadStatus('uploading');
        setUploadProgress(0);
        
        try {
          await sermonsApi.uploadVideo(sermonId, videoFile, (progress) => {
            setUploadProgress(progress);
            if (progress >= 95) {
              setUploadStatus('processing');
            }
          });
          setUploadStatus('done');
          toast.success('Video uploaded successfully!');
        } catch (uploadError) {
          console.error('Video upload error:', uploadError);
          setUploadStatus('error');
          toast.error(`Video upload failed: ${uploadError.message}. You can try uploading again by editing this sermon.`);
          // Don't close dialog - let user see the error and decide what to do
          setIsSubmitting(false);
          loadSermons(); // Refresh to show the sermon without video
          return; // Stop here so user can retry or close manually
        }
      }
      
      // Step 3: Upload thumbnail if selected
      if (thumbnailFile) {
        try {
          await sermonsApi.uploadThumbnail(sermonId, thumbnailFile);
          toast.success('Thumbnail uploaded');
        } catch (thumbError) {
          toast.error(`Thumbnail upload failed: ${thumbError.message}`);
        }
      }
      
      // Refresh list and close dialog
      setDialogOpen(false);
      resetForm();
      loadSermons();
      
    } catch (error) {
      toast.error(`Failed to save sermon: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sermon?')) return;

    try {
      await sermonsApi.delete(id);
      toast.success('Sermon deleted');
      loadSermons();
    } catch (error) {
      toast.error('Failed to delete sermon');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getVideoStatusDisplay = (sermon) => {
    if (sermon.video_url && sermon.video_url.startsWith('/api/uploads')) {
      return <span className="text-green-600 text-sm flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Uploaded</span>;
    }
    if (sermon.video_url && (sermon.video_url.includes('youtube') || sermon.video_url.includes('youtu.be'))) {
      return <span className="text-red-600 text-sm flex items-center gap-1"><Youtube className="h-3 w-3" /> YouTube</span>;
    }
    if (sermon.video_url) {
      return (
        <a href={sermon.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm flex items-center gap-1">
          External <ExternalLink className="h-3 w-3" />
        </a>
      );
    }
    return <span className="text-stone-400 text-sm">No video</span>;
  };

  const handleYouTubeImport = async () => {
    if (!youtubeUrl.trim()) {
      toast.error('Please enter a YouTube playlist URL');
      return;
    }
    
    setIsImporting(true);
    try {
      const result = await sermonsApi.importYouTube(youtubeUrl);
      toast.success(result.message);
      setYoutubeDialogOpen(false);
      setYoutubeUrl('');
      loadSermons();
    } catch (error) {
      toast.error(error.message || 'Failed to import from YouTube');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Sermons</h1>
          <p className="text-stone-600">Manage sermon recordings</p>
        </div>
        <div className="flex gap-2">
          {/* YouTube Import Button */}
          <Dialog open={youtubeDialogOpen} onOpenChange={setYoutubeDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                <Youtube className="h-4 w-4 mr-2" />
                Import from YouTube
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Import from YouTube Playlist</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>YouTube Playlist URL</Label>
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://youtube.com/playlist?list=..."
                    disabled={isImporting}
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Paste a public YouTube playlist URL. Videos already imported will be skipped.
                  </p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-amber-800">Video Title Format:</p>
                  <p className="text-amber-700">2024, January 28 - Sermon Title</p>
                  <p className="text-xs text-amber-600 mt-1">
                    The date will be automatically extracted from titles in this format.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setYoutubeDialogOpen(false)} disabled={isImporting}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleYouTubeImport} 
                    className="bg-red-600 hover:bg-red-700"
                    disabled={isImporting}
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Import className="h-4 w-4 mr-2" />
                        Import Videos
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Add Sermon Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-700 hover:bg-amber-800" data-testid="add-sermon-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Sermon
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSermon ? 'Edit Sermon' : 'Add New Sermon'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Sermon title"
                    required
                    disabled={isSubmitting}
                    data-testid="sermon-title-input"
                  />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    disabled={isSubmitting}
                    data-testid="sermon-date-input"
                  />
                </div>
                <div>
                  <Label>Speaker</Label>
                  <Input
                    value={formData.speaker}
                    onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
                    placeholder="Speaker name"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Sermon description"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              {/* Thumbnail Upload */}
              <div>
                <Label>Thumbnail</Label>
                <div className="mt-2">
                  {thumbnailFile ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <Upload className="h-5 w-5 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-700">{thumbnailFile.name}</p>
                        <p className="text-xs text-blue-600">{formatFileSize(thumbnailFile.size)}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setThumbnailFile(null);
                          if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
                        }}
                        disabled={isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : formData.thumbnail_url ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.thumbnail_url}
                        alt="Thumbnail"
                        className="h-32 rounded-lg object-cover"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute -top-2 -right-2"
                        onClick={() => setFormData({ ...formData, thumbnail_url: '' })}
                        disabled={isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <Input
                        ref={thumbnailInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailSelect}
                        className="max-w-xs"
                        disabled={isSubmitting}
                        data-testid="thumbnail-file-input"
                      />
                      <span className="text-stone-500 text-sm">or</span>
                      <Input
                        value={formData.thumbnail_url}
                        onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                        placeholder="Image URL"
                        className="flex-1"
                        disabled={isSubmitting}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Video Upload */}
              <div>
                <Label>Video</Label>
                <div className="mt-2 space-y-3">
                  {videoFile ? (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <Video className="h-6 w-6 text-green-600" />
                        <div className="flex-1">
                          <p className="font-medium text-green-800">{videoFile.name}</p>
                          <p className="text-sm text-green-600">{formatFileSize(videoFile.size)}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setVideoFile(null);
                            if (videoInputRef.current) videoInputRef.current.value = '';
                          }}
                          disabled={isSubmitting}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Upload progress */}
                      {uploadStatus && (
                        <div className="mt-3">
                          <Progress value={uploadProgress} className="h-2" />
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-sm text-green-700">
                              {uploadStatus === 'uploading' && `Uploading... ${uploadProgress}%`}
                              {uploadStatus === 'processing' && 'Processing...'}
                              {uploadStatus === 'done' && 'Upload complete!'}
                              {uploadStatus === 'error' && 'Upload failed'}
                            </span>
                            {uploadStatus === 'uploading' && (
                              <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                            )}
                            {uploadStatus === 'done' && (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : formData.video_url && formData.video_url.startsWith('/api/uploads') ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-green-700">Video uploaded</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setFormData({ ...formData, video_url: '' })}
                        disabled={isSubmitting}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label className="text-sm text-stone-500">Upload Video File (up to 3GB)</Label>
                        <Input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          onChange={handleVideoSelect}
                          disabled={isSubmitting}
                          data-testid="video-file-input"
                        />
                        <p className="text-xs text-stone-400 mt-1">
                          Supports videos up to 3GB - perfect for 75-minute sermons from your phone
                        </p>
                      </div>
                      <div className="text-center text-stone-500">or</div>
                      <div>
                        <Label className="text-sm text-stone-500">YouTube/Facebook URL</Label>
                        <Input
                          value={formData.video_url}
                          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                          placeholder="https://youtube.com/watch?v=... or https://facebook.com/..."
                          disabled={isSubmitting}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <Label>Duration (optional)</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 1:15:00"
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-700 hover:bg-amber-800" 
                  disabled={isSubmitting}
                  data-testid="sermon-save-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {uploadStatus === 'uploading' ? 'Uploading Video...' : 'Saving...'}
                    </>
                  ) : (
                    editingSermon ? 'Update Sermon' : 'Create Sermon'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Delete Selected Bar */}
      {selectedSermons.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-red-800 font-medium">
            {selectedSermons.size} sermon{selectedSermons.size > 1 ? 's' : ''} selected
          </span>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleDeleteSelected}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </>
            )}
          </Button>
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
          </CardContent>
        </Card>
      ) : sermons.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox 
                    checked={selectedSermons.size === sermons.length && sermons.length > 0}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead>Thumbnail</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Speaker</TableHead>
                <TableHead>Video</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sermons.map((sermon) => (
                <TableRow key={sermon.id} className={selectedSermons.has(sermon.id) ? 'bg-red-50' : ''}>
                  <TableCell>
                    <Checkbox 
                      checked={selectedSermons.has(sermon.id)}
                      onCheckedChange={() => toggleSelectSermon(sermon.id)}
                      aria-label={`Select ${sermon.title}`}
                    />
                  </TableCell>
                  <TableCell>
                    {sermon.thumbnail_url ? (
                      <img
                        src={sermon.thumbnail_url}
                        alt={sermon.title}
                        className="h-12 w-20 object-cover rounded"
                      />
                    ) : (
                      <div className="h-12 w-20 bg-stone-200 rounded flex items-center justify-center">
                        <Video className="h-5 w-5 text-stone-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{sermon.title}</TableCell>
                  <TableCell>{formatDate(sermon.date)}</TableCell>
                  <TableCell>{sermon.speaker}</TableCell>
                  <TableCell>{getVideoStatusDisplay(sermon)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(sermon)} data-testid={`edit-sermon-${sermon.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(sermon.id)} data-testid={`delete-sermon-${sermon.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <Video className="h-12 w-12 text-stone-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-800 mb-2">No Sermons Yet</h3>
            <p className="text-stone-600 mb-4">Get started by adding your first sermon.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800">
              <Plus className="h-4 w-4 mr-2" />
              Add Sermon
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSermons;
